import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowDown, Check } from "lucide-react";
import { Magnetic } from "@/components/fx/Magnetic";
import { SplitLines } from "@/components/fx/Reveal";
import { LazyNetwork } from "@/components/fx/LazyNetwork";
import { scrollToId, useMedia } from "@/lib/motion";

const FunnelCanvas = lazy(() => import("@/components/fx/FunnelCanvas"));

const PHASES = [
  { n: 160, label: "Candidature" },
  { n: 64, label: "Domande filtro" },
  { n: 28, label: "CV e video" },
  { n: 12, label: "Test attitudinale" },
  { n: 6, label: "Colloquio" },
  { n: 4, label: "Assunti" },
];
const LAST = PHASES.length - 1;
const REASSURE = ["Qualsiasi ruolo, qualsiasi settore", "Paghi a risultato", "Prima chiamata gratuita"];

const useTween = (value) => {
  const [v, setV] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    let raf;
    const a = from.current, t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / 700);
      const cur = Math.round(a + (value - a) * (1 - Math.pow(1 - p, 3)));
      setV(cur);
      from.current = cur;
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return v;
};

const usePhase = (wrapRef, cardRef, pinned, reduced) => {
  const [phase, setPhase] = useState(reduced ? LAST : 0);
  useEffect(() => {
    if (reduced) { setPhase(LAST); return; }
    if (!pinned) {
      let k = 0, timer;
      const tick = () => { k = k >= LAST + 2 ? 0 : k + 1; setPhase(Math.min(k, LAST)); };
      const start = setTimeout(() => { tick(); timer = setInterval(tick, 1400); }, 1200);
      return () => { clearTimeout(start); clearInterval(timer); };
    }
    let intro = 0;
    const update = () => {
      const r = wrapRef.current.getBoundingClientRect();
      const total = Math.max(1, r.height - window.innerHeight);
      const p = Math.min(1, Math.max(0, -r.top / total));
      const s = Math.min(LAST, Math.floor((p / 0.82) * LAST + 0.0001));
      setPhase(Math.max(s, intro));
    };
    const t = setTimeout(() => { intro = 1; update(); }, 2200);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => { clearTimeout(t); window.removeEventListener("scroll", update); };
  }, [pinned, reduced, wrapRef, cardRef]);
  return phase;
};

const Instrument = ({ phase, reduced, cardRef, pinned }) => {
  const count = useTween(PHASES[phase].n);
  const done = phase === LAST;
  return (
    <div ref={cardRef} data-theme="dark" data-testid="hero-funnel" className="rl-grain relative h-[500px] overflow-hidden rounded-[24px] bg-[#13352F] text-white shadow-[0_40px_80px_-40px_rgba(19,53,47,0.7)] sm:h-[560px] lg:h-[min(76vh,700px)]">
      <LazyNetwork density={22000} maxDist={110} amberRatio={0.03} className="opacity-60" />
      <Suspense fallback={null}>
        <FunnelCanvas phase={phase} reduced={reduced} />
      </Suspense>
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-5 sm:p-6">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.16em] text-[#BCE3D6]">
          <span className={`mr-2 inline-block h-1.5 w-1.5 rounded-full ${done ? "bg-[#EF9F27]" : "bg-[#BCE3D6] rl-blink"}`} />
          {done ? "Match trovato" : "Selezione in corso"}
          <div className="mt-1 text-[#BCE3D6]/60">Fase {String(phase + 1).padStart(2, "0")} / {String(PHASES.length).padStart(2, "0")}</div>
        </div>
        <div className="text-right">
          <div className={`font-mono-data text-5xl font-medium leading-none tracking-tight sm:text-6xl ${done ? "text-[#EF9F27]" : "text-white"}`} data-testid="hero-funnel-counter" aria-live="polite">
            {count}
          </div>
          <div className="mt-2 font-mono-data text-[11px] uppercase tracking-[0.16em] text-[#BCE3D6]" data-testid="hero-funnel-phase-label">
            {PHASES[phase].label}
          </div>
        </div>
      </div>
      <ol className="pointer-events-none absolute bottom-0 left-0 right-0 flex flex-wrap gap-x-3 gap-y-1.5 p-5 font-mono-data text-[10.5px] uppercase tracking-[0.12em] sm:p-6" aria-label="Fasi del filtro">
        {PHASES.map((p, i) => (
          <li key={p.label} data-testid={`hero-phase-${i}`} className={`transition-colors duration-500 ${i === phase ? (done ? "text-[#EF9F27]" : "text-white") : i < phase ? "text-[#BCE3D6]/70" : "text-[#BCE3D6]/30"}`}>
            {p.n}
            {i < LAST && <span className="ml-3 text-[#BCE3D6]/30">→</span>}
          </li>
        ))}
      </ol>
      {pinned && !done && !reduced && (
        <div className="pointer-events-none absolute bottom-14 left-1/2 -translate-x-1/2 font-mono-data text-[10.5px] uppercase tracking-[0.16em] text-[#BCE3D6]/70">
          <ArrowDown size={12} className="mr-1 inline animate-bounce" /> Scorri per filtrare
        </div>
      )}
    </div>
  );
};

const DEFAULT_LINES = ["Trovare brave", "persone non è", "fortuna.", <span key="m" className="text-[#0F6E56]">È un metodo.</span>];
const DEFAULT_SUB = "Recruiting Lab trova, filtra e testa i candidati per la tua azienda. Tu incontri solo le persone già pronte per il ruolo: niente CV da leggere, niente colloqui a vuoto.";
const go = (id) => (e) => { e.preventDefault(); scrollToId(id); };

export const Hero = ({
  eyebrow = "Recruiting Lab Italia", lines = DEFAULT_LINES, sub = DEFAULT_SUB, primary = "Raccontaci chi cerchi",
  note = null, secondary = { label: "Vedi come lavoriamo", target: "metodo" }, chips = REASSURE,
}) => {
  const wrapRef = useRef(null);
  const cardRef = useRef(null);
  const desktop = useMedia("(min-width: 1024px)");
  const reduced = useMedia("(prefers-reduced-motion: reduce)");
  const pinned = desktop && !reduced;
  const phase = usePhase(wrapRef, cardRef, pinned, reduced);

  return (
    <section id="top" ref={wrapRef} className={`relative ${pinned ? "h-[280vh]" : ""}`} data-testid="hero-section">
      <div className={`${pinned ? "sticky top-0 h-screen min-h-[700px]" : ""}`}>
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[#BCE3D6]/50 blur-[120px]" />
        </div>
        <div className="rl-container relative grid h-full items-center gap-10 pb-12 pt-[104px] lg:grid-cols-12 lg:gap-8 lg:pb-8 lg:pt-[88px]">
          <div className="lg:col-span-7 lg:pr-6">
            <p className="rl-eyebrow text-[#0F6E56]" data-testid="hero-eyebrow">{eyebrow}</p>
            <SplitLines as="h1" className="rl-display mt-6 text-[clamp(2.4rem,4.7vw,5.1rem)] text-[#2C2C2A]" lines={lines} data-testid="hero-title" />
            <p className="rl-fade is-in mt-8 max-w-[560px] text-[17px] leading-relaxed text-[#2C2C2A]/85 sm:text-lg" data-testid="hero-subtitle">{sub}</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <Magnetic>
                <a href="#richiedi" onClick={go("richiedi")} className="rl-btn rl-btn-amber w-full !h-14 !px-8 !text-base sm:w-auto" data-testid="hero-primary-cta">
                  {primary} <ArrowRight size={18} className="rl-arrow" />
                </a>
              </Magnetic>
              {secondary && (
                <a href={`#${secondary.target}`} onClick={go(secondary.target)} className="rl-btn rl-btn-ghost !h-14 !px-7" data-testid="hero-secondary-cta">
                  {secondary.label}
                </a>
              )}
            </div>
            {note && <p className="mt-4 font-mono-data text-[12px] uppercase tracking-[0.12em] text-[#6B6961]" data-testid="hero-cta-note">{note}</p>}
            <ul className="mt-9 flex flex-col gap-2.5 text-[14px] text-[#2C2C2A] sm:flex-row sm:flex-wrap sm:gap-x-6" data-testid="hero-reassurances">
              {chips.map((r) => (
                <li key={r} className="inline-flex items-center gap-2">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#BCE3D6] text-[#0F6E56]"><Check size={12} strokeWidth={3} /></span>
                  {r}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <Instrument phase={phase} reduced={reduced} cardRef={cardRef} pinned={pinned} />
          </div>
        </div>
      </div>
    </section>
  );
};
